import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { useAPI } from "../contexts/APIContext";
import { useAuth } from "../contexts/AuthContext";
import { useCityHelpersSoundManager } from "../components/games/city-helpers/SoundManager";
import {
  loadDayState,
  saveDayState,
  scenariosForIDs,
  SEL_SKILLS,
} from "../components/games/city-helpers/gameUtils";
import CityScape from "../components/games/city-helpers/CityScape";
import ActivityView from "../components/games/city-helpers/ActivityView";
import { Sprite, SpeakerIcon, MuteIcon, CheckIcon } from "../components/games/city-helpers/fx";
import BackToGamesButton from "../components/games/BackToGamesButton";
import "./CityHelpersPage.css";

const F = { fontFamily: "'Fredoka', sans-serif" };

const CONFETTI = Array.from({ length: 22 }, (_, i) => ({
  id: i,
  left: `${5 + ((i * 31) % 90)}%`,
  delay: `${(i % 6) * 0.12}s`,
  color: ["#ff8f6b", "#ffd43b", "#5fce6a", "#4dabf7", "#b197fc", "#ff7a8a"][i % 6],
}));

// The five friends the child will meet — their faces ARE the invitation.
const INTRO_FRIENDS = [
  { asset: "ch_maya_happy", ring: "#4dabf7" },
  { asset: "ch_mrlee_happy", ring: "#5fce6a" },
  { asset: "ch_grandma_happy", ring: "#b197fc" },
  { asset: "ch_newkid_happy", ring: "#ffd43b" },
  { asset: "ch_puppy_happy", ring: "#ff8f6b" },
];

const IntroModal = ({ onStart }) => (
  <div className="ch-modal-backdrop" role="dialog" aria-modal="true" aria-labelledby="ch-intro-title">
    <motion.div
      className="ch-modal"
      initial={{ scale: 0.85, opacity: 0, y: 20 }}
      animate={{ scale: 1, opacity: 1, y: 0 }}
      transition={{ type: "spring", stiffness: 220, damping: 18 }}
    >
      <div className="ch-friend-row" aria-hidden="true">
        {INTRO_FRIENDS.map((friend, i) => (
          <motion.span
            key={friend.asset}
            className="ch-friend-bubble"
            style={{ "--friend-ring": friend.ring, zIndex: i === 2 ? 3 : i === 1 || i === 3 ? 2 : 1 }}
            initial={{ scale: 0, y: 18, rotate: i % 2 ? 10 : -10 }}
            animate={{ scale: 1, y: 0, rotate: (i - 2) * 5 }}
            transition={{ type: "spring", stiffness: 320, damping: 15, delay: 0.12 + i * 0.08 }}
          >
            <img
              src={`/assets/games/city-helpers/${friend.asset}.png`}
              alt=""
              draggable={false}
            />
          </motion.span>
        ))}
      </div>
      <h2 id="ch-intro-title">Welcome to Pixley City!</h2>
      <p>Five friends need a little help today. Let&apos;s explore the city and be kind to each one!</p>
      <div className="ch-how-list">
        <div className="ch-how-step"><span className="ch-how-number">1</span><span>Tap a glowing spot to meet a friend.</span></div>
        <div className="ch-how-step"><span className="ch-how-number">2</span><span>Do something kind — drag, tap, or choose.</span></div>
        <div className="ch-how-step"><span className="ch-how-number">3</span><span>Help all five and the whole city sparkles!</span></div>
      </div>
      <button type="button" className="ch-primary-btn ch-modal-btn" onClick={onStart}>Let&apos;s help!</button>
    </motion.div>
  </div>
);

export default function CityHelpersPage() {
  const navigate = useNavigate();
  const { apiClient } = useAPI();
  const { user, refreshUserData } = useAuth();
  const sounds = useCityHelpersSoundManager();

  const profileKey = useMemo(() => {
    const id = user?.id || user?.user_id || user?.uid;
    return id ? String(id) : "default";
  }, [user]);

  const [day, setDay] = useState(() => loadDayState(profileKey));
  const [started, setStarted] = useState(() => {
    const initial = loadDayState(profileKey);
    // A day already finished on reopen skips the how-to and celebrates.
    return initial.scenarioIDs.length > 0 && initial.completedIDs.length >= initial.scenarioIDs.length;
  });
  const [activeId, setActiveId] = useState(null);
  const [parallax, setParallax] = useState(0);
  const [showResults, setShowResults] = useState(false);
  const [confettiKey, setConfettiKey] = useState(0);
  const [offscreen, setOffscreen] = useState({ left: false, right: false });
  const [focusOrigin, setFocusOrigin] = useState({ x: 50, y: 55 });

  const scrollRef = useRef(null);
  const progressSentRef = useRef(false);
  const sweptRef = useRef(false);

  const scenarios = useMemo(() => scenariosForIDs(day.scenarioIDs), [day.scenarioIDs]);
  const completedSet = useMemo(() => new Set(day.completedIDs), [day.completedIDs]);
  const completedCount = day.completedIDs.length;
  const total = scenarios.length;
  const isDayComplete = total > 0 && completedCount >= total;
  const activeScenario = useMemo(
    () => scenarios.find((s) => s.id === activeId) || null,
    [scenarios, activeId]
  );

  // Reload today's set when the profile changes or the tab returns (day rollover).
  useEffect(() => {
    setDay(loadDayState(profileKey));
  }, [profileKey]);

  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === "visible") setDay(loadDayState(profileKey));
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => document.removeEventListener("visibilitychange", onVisible);
  }, [profileKey]);

  useEffect(() => () => sounds.stop(), [sounds]);

  const scrollToScenario = useCallback((id, behavior = "smooth") => {
    const container = scrollRef.current;
    const el = container?.querySelector(`[data-scenario="${id}"]`);
    if (!container || !el) return;
    const target = el.offsetLeft - container.clientWidth / 2 + el.offsetWidth / 2;
    container.scrollTo({ left: Math.max(0, target), behavior });
  }, []);

  // A bouncing "More helpers" hint whenever incomplete markers sit off-screen.
  const updateHints = useCallback(() => {
    const container = scrollRef.current;
    if (!container) return;
    const viewL = container.scrollLeft;
    const viewR = viewL + container.clientWidth;
    let anyLeft = false;
    let anyRight = false;
    scenarios.forEach((s) => {
      if (completedSet.has(s.id)) return;
      const el = container.querySelector(`[data-scenario="${s.id}"]`);
      if (!el) return;
      const cx = el.offsetLeft + el.offsetWidth / 2;
      if (cx < viewL + 50) anyLeft = true;
      else if (cx > viewR - 50) anyRight = true;
    });
    setOffscreen({ left: anyLeft, right: anyRight });
  }, [scenarios, completedSet]);

  const scrollToNearestRemaining = useCallback(
    (side) => {
      const container = scrollRef.current;
      if (!container) return;
      const viewL = container.scrollLeft;
      const viewR = viewL + container.clientWidth;
      const remaining = scenarios
        .filter((s) => !completedSet.has(s.id))
        .map((s) => {
          const el = container.querySelector(`[data-scenario="${s.id}"]`);
          return el ? { id: s.id, cx: el.offsetLeft + el.offsetWidth / 2 } : null;
        })
        .filter(Boolean);
      const off = remaining.filter((r) => (side === "left" ? r.cx < viewL + 50 : r.cx > viewR - 50));
      if (!off.length) return;
      off.sort((a, b) => (side === "left" ? b.cx - a.cx : a.cx - b.cx));
      scrollToScenario(off[0].id);
    },
    [scenarios, completedSet, scrollToScenario]
  );

  const handleScroll = useCallback(
    (e) => {
      setParallax(e.currentTarget.scrollLeft);
      updateHints();
    },
    [updateHints]
  );

  // First open: a gentle guided pan across the city, then settle on the first
  // helper — so the child sees the whole map is explorable.
  useEffect(() => {
    if (!started || sweptRef.current || scenarios.length === 0) return;
    const container = scrollRef.current;
    if (!container) return;
    sweptRef.current = true;
    const settle = () => {
      const first = scenarios.find((s) => !completedSet.has(s.id)) || scenarios[0];
      if (first) scrollToScenario(first.id);
      updateHints();
    };
    const id1 = window.setTimeout(() => {
      container.scrollTo({ left: container.scrollWidth, behavior: "smooth" });
    }, 500);
    const id2 = window.setTimeout(settle, 1900);
    return () => {
      window.clearTimeout(id1);
      window.clearTimeout(id2);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [started, scenarios.length]);

  useEffect(() => {
    updateHints();
  }, [updateHints]);

  const persistDayComplete = useCallback(async () => {
    if (progressSentRef.current || !apiClient) return;
    progressSentRef.current = true;
    try {
      await apiClient.postGameProgress("CITY_HELPERS", 100, true, null);
      window.dispatchEvent(new CustomEvent("games:progress-updated"));
      refreshUserData?.();
    } catch {
      // The celebration is already on screen — never let saving break play.
      progressSentRef.current = false;
    }
  }, [apiClient, refreshUserData]);

  // When the day becomes complete, celebrate and (once) report the star.
  useEffect(() => {
    if (!isDayComplete) return;
    setShowResults(true);
    setConfettiKey((k) => k + 1);
    sounds.playCelebrate();
    if (!day.starRequested) {
      persistDayComplete();
      setDay((prev) => {
        const next = { ...prev, starRequested: true };
        saveDayState(next, profileKey);
        return next;
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDayComplete]);

  const openScenario = useCallback(
    (scenario) => {
      if (activeId) return;
      sounds.playPop(0.6);
      // Push the camera in on the tapped spot: centre it, aim the zoom origin
      // at its ground line, then fade the activity in over the zoomed city.
      const container = scrollRef.current;
      const el = container?.querySelector(`[data-scenario="${scenario.id}"]`);
      if (container && el) {
        const y = ((el.offsetTop + el.offsetHeight / 2) / container.scrollHeight) * 100;
        setFocusOrigin({ x: 50, y: Math.max(20, Math.min(85, y)) });
      }
      scrollToScenario(scenario.id);
      window.setTimeout(() => setActiveId(scenario.id), 420);
    },
    [activeId, scrollToScenario, sounds]
  );

  const closeActivity = useCallback(() => {
    sounds.stop();
    setActiveId(null);
  }, [sounds]);

  const completeActivity = useCallback(() => {
    const scenario = activeScenario;
    setActiveId(null);
    if (!scenario || day.completedIDs.includes(scenario.id)) return;
    setDay((prev) => {
      const next = { ...prev, completedIDs: [...prev.completedIDs, scenario.id] };
      saveDayState(next, profileKey);
      return next;
    });
  }, [activeScenario, day.completedIDs, profileKey]);

  const handleExit = useCallback(() => {
    sounds.stop();
    navigate("/games");
  }, [navigate, sounds]);

  const finishAndLeave = useCallback(() => {
    setShowResults(false);
    handleExit();
  }, [handleExit]);

  return (
    <main className="ch-page" style={F}>
      <header className="ch-topbar">
        <BackToGamesButton onClick={handleExit} accent="#e85a3c" />
        <div className="ch-title-wrap">
          <h1>Pixley City</h1>
          <p>{isDayComplete ? "Every moment helped — hooray!" : "Find today's helper moments"}</p>
        </div>
        <button
          type="button"
          className="ch-icon-button"
          onClick={sounds.toggleMute}
          aria-label={sounds.muted ? "Turn sound on" : "Mute sounds"}
          aria-pressed={sounds.muted}
        >
          <span className="ch-icon-glyph" aria-hidden="true">{sounds.muted ? <MuteIcon /> : <SpeakerIcon />}</span>
        </button>
      </header>

      <section className="ch-board" aria-label="City Helpers game">
        <div
          className={`ch-city-scroll ${activeId ? "is-focused" : ""}`}
          ref={scrollRef}
          onScroll={handleScroll}
          style={{ transformOrigin: `${focusOrigin.x}% ${focusOrigin.y}%` }}
        >
          <CityScape
            scenarios={scenarios}
            completedIDs={completedSet}
            dayComplete={isDayComplete}
            parallax={parallax}
            onTapHotspot={openScenario}
          />
        </div>

        {!activeId && !isDayComplete && (
          <>
            {offscreen.left && (
              <button
                type="button"
                className="ch-more-arrow is-left"
                onClick={() => scrollToNearestRemaining("left")}
                aria-label="More helpers this way"
              >
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden>
                  <path d="M12.5 4 6.5 10l6 6" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </button>
            )}
            {offscreen.right && (
              <button
                type="button"
                className="ch-more-arrow is-right"
                onClick={() => scrollToNearestRemaining("right")}
                aria-label="More helpers this way"
              >
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden>
                  <path d="m7.5 4 6 6-6 6" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </button>
            )}
          </>
        )}

        <div className="ch-hud">
          <div className="ch-happiness" aria-label={`${completedCount} of ${total} helpers done`}>
            <div className="ch-happiness-head">
              <span>City Happiness</span>
            </div>
            <div className="ch-happiness-track" role="progressbar" aria-valuemin={0} aria-valuemax={total} aria-valuenow={completedCount}>
              <div className="ch-happiness-fill" style={{ width: `${total ? (completedCount / total) * 100 : 0}%` }} />
            </div>
            <span className="ch-happiness-count">{completedCount} / {total}</span>
          </div>

          <div className="ch-objectives">
            <div className="ch-objectives-head">Today&apos;s Helpers</div>
            {scenarios.map((scenario) => {
              const done = completedSet.has(scenario.id);
              return (
                <button
                  key={scenario.id}
                  type="button"
                  className={`ch-objective ${done ? "is-done" : ""}`}
                  onClick={() => scrollToScenario(scenario.id)}
                >
                  <span className="ch-objective-check" aria-hidden="true">{done ? <CheckIcon size={14} /> : "◦"}</span>
                  <span className="ch-objective-icon"><Sprite name={scenario.iconAsset} /></span>
                  <span className="ch-objective-title">{scenario.title}</span>
                </button>
              );
            })}
          </div>
        </div>

        <AnimatePresence>
          {activeScenario && (
            <ActivityView
              key={activeScenario.id}
              scenario={activeScenario}
              sounds={sounds}
              onComplete={completeActivity}
              onClose={closeActivity}
            />
          )}
        </AnimatePresence>

        <AnimatePresence>
          {showResults && (
            <motion.div
              className="ch-results-backdrop"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            >
              <div className="ch-confetti" aria-hidden="true">
                {CONFETTI.map((bit) => (
                  <i key={`${confettiKey}-${bit.id}`} style={{ left: bit.left, animationDelay: bit.delay, background: bit.color }} />
                ))}
              </div>
              <motion.div
                className="ch-results"
                initial={{ scale: 0.85, y: 24, opacity: 0 }}
                animate={{ scale: 1, y: 0, opacity: 1 }}
                transition={{ type: "spring", stiffness: 200, damping: 18 }}
              >
                <h2>The city is sparkling!</h2>
                <p>You did all {total} helper moments today. Every kind act made someone smile!</p>
                <div className="ch-results-skills">
                  {scenarios.map((scenario) => (
                    <div key={scenario.id} className="ch-results-skill">
                      <span>{scenario.title} · {SEL_SKILLS[scenario.skill].label}</span>
                    </div>
                  ))}
                </div>
                <div className="ch-results-actions">
                  <button type="button" className="ch-ghost-btn" onClick={() => setShowResults(false)}>Keep exploring</button>
                  <button type="button" className="ch-primary-btn" onClick={finishAndLeave}>Done</button>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        {!started && <IntroModal onStart={() => setStarted(true)} />}
      </section>
    </main>
  );
}
